import { useState, useEffect, useCallback } from "react";
import { ChevronRight, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";

type Run = {
  id: number;
  owner: string;
  repo: string;
  prNumber: number;
  workflowKind: string;
  status: string;
  currentStep: string;
  runId: string;
  epoch: number;
  lastReviewedSha: string;
  ciPendingDeferralSha: string;
  wedgeReason: string;
  updatedAt: string;
};

type Step = {
  stepName: string;
  stepIndex: number;
  kind: string;
  status: string;
  attempt: number;
  startedAt: string;
  completedAt: string | null;
  error: string;
  detail: string;
};

const STATUS_STYLE: Record<string, string> = {
  running:         "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  queued:          "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  done:            "bg-muted text-muted-foreground",
  wedged:          "bg-orange-500/15 text-orange-700 dark:text-orange-400",
  force_finalized: "bg-red-500/15 text-red-700 dark:text-red-400",
};

const STEP_STYLE: Record<string, string> = {
  in_progress: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  complete:    "bg-green-500/15 text-green-700 dark:text-green-400",
  failed:      "bg-red-500/15 text-red-700 dark:text-red-400",
  skipped:     "bg-muted text-muted-foreground",
};

function fmt(iso: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function useRuns(refreshKey: number) {
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch("/api/runs/?limit=50")
      .then(r => r.ok ? r.json() : { runs: [] })
      .then(d => { if (alive) setRuns(d.runs ?? []); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshKey]);
  return { runs, loading };
}

function useSteps(runId: string | null) {
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!runId) { setSteps(null); return; }
    let alive = true;
    setLoading(true);
    fetch(`/api/runs/${encodeURIComponent(runId)}/steps`)
      .then(r => r.ok ? r.json() : { steps: [] })
      .then(d => { if (alive) setSteps(d.steps ?? []); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [runId]);
  return { steps, loading };
}

export default function Runs() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const { runs, loading } = useRuns(refreshKey);
  const { steps, loading: stepsLoading } = useSteps(expanded);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Review runs"
        description="PR-review workflow runs driven by the CEL trigger feed. Each run records its actual steps — click a row to inspect the trail."
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : runs.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border py-16 text-center">
          <p className="text-sm text-muted-foreground">No runs yet. A matched trigger will start one.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                <th className="py-2 pl-4 pr-2 text-left font-medium w-8"></th>
                <th className="py-2 px-3 text-left font-medium">Repository</th>
                <th className="py-2 px-3 text-left font-medium">PR</th>
                <th className="py-2 px-3 text-left font-medium">Status</th>
                <th className="py-2 px-3 text-left font-medium">Current step</th>
                <th className="py-2 px-3 text-left font-medium">Epoch</th>
                <th className="py-2 px-3 text-left font-medium">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {runs.map(r => {
                const open = expanded === r.runId;
                const hasRun = !!r.runId;
                return (
                  <>
                    <tr
                      key={r.id}
                      className={`hover:bg-muted/20 transition-colors ${hasRun ? "cursor-pointer" : ""}`}
                      onClick={() => hasRun && setExpanded(open ? null : r.runId)}
                    >
                      <td className="py-3 pl-4 pr-2 text-muted-foreground">
                        {hasRun && <ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} />}
                      </td>
                      <td className="py-3 px-3 font-mono text-xs">{r.owner}/{r.repo}</td>
                      <td className="py-3 px-3 tabular-nums">#{r.prNumber}</td>
                      <td className="py-3 px-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[r.status] ?? "bg-muted text-muted-foreground"}`}>
                          {r.status}
                        </span>
                        {r.wedgeReason && <span className="ml-2 text-xs text-muted-foreground">{r.wedgeReason}</span>}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">{r.currentStep || "—"}</td>
                      <td className="py-3 px-3 tabular-nums text-muted-foreground">{r.epoch}</td>
                      <td className="py-3 px-3 whitespace-nowrap tabular-nums text-muted-foreground">{fmt(r.updatedAt)}</td>
                    </tr>
                    {open && (
                      <tr key={`${r.id}-steps`} className="bg-muted/10">
                        <td colSpan={7} className="px-6 py-3">
                          {stepsLoading ? (
                            <span className="inline-flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> loading steps…</span>
                          ) : !steps || steps.length === 0 ? (
                            <span className="text-xs text-muted-foreground">No steps recorded for this run.</span>
                          ) : (
                            <ol className="space-y-1">
                              {steps.map((s, i) => (
                                <li key={i} className="flex items-center gap-2 text-xs">
                                  <span className="w-5 text-right tabular-nums text-muted-foreground/60">{s.stepIndex}</span>
                                  <Badge variant="outline" className={`text-xs ${STEP_STYLE[s.status] ?? ""}`}>{s.status}</Badge>
                                  <span className="font-mono">{s.stepName}</span>
                                  <span className="text-muted-foreground/50">[{s.kind}]</span>
                                  {s.error && <span className="text-red-600 dark:text-red-400">{s.error}</span>}
                                </li>
                              ))}
                            </ol>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        <button onClick={refresh} className="underline-offset-2 hover:underline">refresh</button>
      </div>
    </div>
  );
}
